import { useState, useEffect } from 'react';
import { useApplications } from '../../hooks/unified/useApplicationsQueries';
import { ApplicationTimeline, ApplicationCard } from '../../components/applications';
import { LoadingSpinner } from '../../components/layout/LoadingSpinner';
import { UserDashboardLayout } from '../../components/layout/UserDashboardLayout';
import { useViewport } from '../../hooks/useViewport';
import { Link } from 'react-router-dom';

export function ApplicationsPage() {
  const { data: applications = [], isLoading: loading, error: queryError, refetch } = useApplications();
  const { isMobile } = useViewport();
  // Default to cards view on mobile for better UX
  const [viewMode, setViewMode] = useState<'timeline' | 'cards'>('timeline');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'status'>('date');
  
  // Set cards view on mobile on initial load
  useEffect(() => {
    if (isMobile) {
      setViewMode('cards');
    }
  }, []);

  const error = queryError ? (queryError as Error).message : null;

  // Filter applications by status
  let filteredApplications = applications.filter(app => {
    if (filterStatus === 'all') return true;
    return app.status === filterStatus;
  });

  // Sort applications
  filteredApplications = [...filteredApplications].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.applied_at || '').getTime() - new Date(a.applied_at || '').getTime();
    } else {
      return a.status.localeCompare(b.status);
    }
  });

  const statusCounts = applications.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);



  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  if (error) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Applications</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => refetch()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-4 sm:p-6">
        {/* Header - Mobile optimized */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Applications</h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {applications.length} Total
            </span>
          </div>
          <p className="text-sm sm:text-base text-gray-600">
            Track and manage your job applications.
          </p>
        </div>

        {/* Stats Overview - Scrollable on mobile */}
        <div className="mb-4 sm:mb-6 -mx-4 sm:mx-0 px-4 sm:px-0">
          <div className="flex sm:grid sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4 overflow-x-auto sm:overflow-visible pb-2 sm:pb-0 mobile-scroll-x">
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-[100px] sm:min-w-0 flex-shrink-0 sm:flex-shrink">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">{applications.length}</div>
              <div className="text-xs sm:text-sm text-gray-500">Total</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-[100px] sm:min-w-0 flex-shrink-0 sm:flex-shrink">
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{statusCounts.submitted || 0}</div>
              <div className="text-xs sm:text-sm text-gray-500">Submitted</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-[100px] sm:min-w-0 flex-shrink-0 sm:flex-shrink">
              <div className="text-xl sm:text-2xl font-bold text-yellow-600">{statusCounts.reviewing || 0}</div>
              <div className="text-xs sm:text-sm text-gray-500">Reviewing</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-[100px] sm:min-w-0 flex-shrink-0 sm:flex-shrink">
              <div className="text-xl sm:text-2xl font-bold text-purple-600">{statusCounts.interview || 0}</div>
              <div className="text-xs sm:text-sm text-gray-500">Interview</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-[100px] sm:min-w-0 flex-shrink-0 sm:flex-shrink">
              <div className="text-xl sm:text-2xl font-bold text-green-600">{statusCounts.offer || 0}</div>
              <div className="text-xs sm:text-sm text-gray-500">Offers</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 min-w-[100px] sm:min-w-0 flex-shrink-0 sm:flex-shrink">
              <div className="text-xl sm:text-2xl font-bold text-red-600">{statusCounts.rejected || 0}</div>
              <div className="text-xs sm:text-sm text-gray-500">Rejected</div>
            </div>
          </div>
        </div>

        {/* Controls - Mobile optimized */}
        <div className="bg-white shadow rounded-lg mb-4 sm:mb-6">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
              {/* Filters - Stack on mobile */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="flex-1 sm:flex-none">
                  <label htmlFor="status-filter" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Filter by Status
                  </label>
                  <select
                    id="status-filter"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2.5 sm:py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg min-h-[44px]"
                  >
                    <option value="all">All Applications</option>
                    <option value="submitted">Submitted</option>
                    <option value="reviewing">Under Review</option>
                    <option value="interview">Interview</option>
                    <option value="offer">Offer</option>
                    <option value="rejected">Rejected</option>
                    <option value="withdrawn">Withdrawn</option>
                    <option value="accepted">Accepted</option>
                    <option value="offered">Offered</option>
                    <option value="hired">Hired</option>
                  </select>
                </div>
                <div className="flex-1 sm:flex-none">
                  <label htmlFor="sort-by" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Sort By
                  </label>
                  <select
                    id="sort-by"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'date' | 'status')}
                    className="block w-full pl-3 pr-10 py-2.5 sm:py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-lg min-h-[44px]"
                  >
                    <option value="date">Date (Newest First)</option>
                    <option value="status">Status</option>
                  </select>
                </div>
              </div>
              {/* View toggle - Hidden on mobile, default to cards */}
              <div className="hidden sm:flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px] ${
                    viewMode === 'timeline'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Timeline View
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px] ${
                    viewMode === 'cards'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Card View
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Applications Display */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white shadow rounded-lg">
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {filterStatus === 'all' ? 'No applications yet' : `No ${filterStatus} applications`}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {filterStatus === 'all' 
                  ? 'Start applying to jobs to track your progress here.'
                  : `You don't have any applications with ${filterStatus} status.`
                }
              </p>
              {filterStatus === 'all' && (
                <div className="mt-6">
                  <Link
                    to="/jobs"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Browse Jobs
                  </Link>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg">
            {viewMode === 'timeline' ? (
              <ApplicationTimeline 
                applications={filteredApplications}
                onUpdate={() => refetch()}
              />
            ) : (
              <div className="p-6">
                <div className="grid gap-6">
                  {filteredApplications.map((application) => (
                    <ApplicationCard
                      key={application.id}
                      application={application}
                      onUpdate={() => refetch()}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </UserDashboardLayout>
  );
}